/**
 * 主应用组件
 */
import React from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { useAppState } from './hooks/useAppState';
import { useAuth } from './hooks/useAuth';
import StepBar from './components/StepBar';
import DocumentAnalysis from './pages/DocumentAnalysis';
import OutlineEdit from './pages/OutlineEdit';
import ContentEdit from './pages/ContentEdit';
import Login from './pages/Login';
import { CurrentUser } from './services/api';
import { draftStorage } from './utils/draftStorage';
import VisualLogin from './visual/VisualLogin';
import VisualWorkspace from './visual/VisualWorkspace';

interface AuthenticatedAppProps {
  user: CurrentUser;
  onLogout: () => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({ user, onLogout }) => {
  draftStorage.setMemberId(user.id);

  const {
    state,
    updateStep,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
    nextStep,
    prevStep,
    resetState,
  } = useAppState();

  const steps = ['标书解析', '目录编辑', '正文编辑'];

  const handleReset = () => {
    if (!window.confirm('确定要清理所有缓存数据并从头开始吗？')) {
      return;
    }

    draftStorage.clearAll();
    resetState();
  };

  const handleLogout = () => {
    draftStorage.setMemberId(null);
    onLogout();
  };

  const renderCurrentPage = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <DocumentAnalysis
            fileContent={state.fileContent}
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            onFileUpload={updateFileContent}
            onAnalysisComplete={updateAnalysisResults}
          />
        );
      case 1:
        return (
          <OutlineEdit
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            outlineData={state.outlineData}
            onOutlineGenerated={updateOutline}
          />
        );
      case 2:
        return (
          <ContentEdit
            outlineData={state.outlineData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 步骤导航 */}
        <div className="sticky top-0 z-50 flex items-center gap-6 bg-white px-6 shadow-sm">
          <div className="min-w-0 flex-1">
          <StepBar steps={steps} currentStep={state.currentStep} />
          </div>
          <div className="flex shrink-0 items-center gap-3 text-sm">
            <span className="text-gray-600">{user.username}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              退出登录
            </button>
          </div>
        </div>

        {/* 页面内容 */}
        <div id="app-main-scroll" className="flex-1 p-6 overflow-y-auto">
          {renderCurrentPage()}
        </div>

        {/* 底部导航按钮 */}
        <div className="sticky bottom-0 z-50 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-between">
            <div className="flex items-center space-x-3">
              {state.currentStep === 0 && (
                <button
                  onClick={handleReset}
                  title="清理所有缓存数据，从头开始"
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  重置
                </button>
              )}

              <button
                onClick={() => updateStep(0)}
                disabled={state.currentStep === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
              >
                首页
              </button>

              <button
                onClick={prevStep}
                disabled={state.currentStep === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                上一步
              </button>
            </div>

            <button
              onClick={nextStep}
              disabled={state.currentStep === steps.length - 1}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              下一步
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 微信新用户首次登录时弹出的绑定用户名密码弹窗
 */
interface BindInfoModalProps {
  onBind: (username: string, password: string) => Promise<void>;
  onLogout: () => void;
}

const BindInfoModal: React.FC<BindInfoModalProps> = ({ onBind, onLogout }) => {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('请输入用户名');
      return;
    }
    if (password.length < 6 || password.length > 16) {
      setError('密码长度需为 6-16 位');
      return;
    }
    if (/^\d+$/.test(password) || /^[a-zA-Z]+$/.test(password)) {
      setError('密码不能纯数字或纯字母');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次密码不一致');
      return;
    }

    setSubmitting(true);
    try {
      await onBind(username.trim(), password);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || '绑定失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">欢迎注册</h2>
          <p className="mt-1 text-sm text-gray-500">
            微信登录成功，请设置用户名和密码，方便下次直接登录
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              用户名
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="请输入用户名"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              密码
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6-16位，不能纯数字或纯字母"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              确认密码
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="请再次输入密码"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onLogout}
              disabled={submitting}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              退出登录
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? '绑定中...' : '完成注册'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function LegacyApp() {
  const { user, isLoading, login, logout, wechatLoginByJwt, needBindInfo, bindInfo } = useAuth();
  const useLegacyUi = process.env.REACT_APP_USE_LEGACY_UI === 'true';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-600">
        正在验证登录状态...
      </div>
    );
  }

  if (!user) {
    return useLegacyUi
      ? <Login onLogin={login} />
      : (
        <VisualLogin
          onLogin={login}
          onWechatLoginByJwt={wechatLoginByJwt}
          wechatLoginEnabled
        />
      );
  }

  draftStorage.setMemberId(user.id);

  const workspace = useLegacyUi
    ? <AuthenticatedApp user={user} onLogout={logout} />
    : <VisualWorkspace user={user} onLogout={logout} />;

  return (
    <>
      {workspace}
      {needBindInfo && (
        <BindInfoModal onBind={bindInfo} onLogout={logout} />
      )}
    </>
  );
}

const RoutedApp: React.FC = () => {
  const { user, isLoading, login, logout, wechatLoginByJwt, needBindInfo, bindInfo } = useAuth();
  const location = useLocation();
  const routeState = location.state as { from?: string } | null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bid-color-page)] flex items-center justify-center text-slate-600">
        正在验证登录状态...
      </div>
    );
  }

  if (!user) {
    if (location.pathname !== '/login') {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }
    return (
      <VisualLogin
        onLogin={login}
        onWechatLoginByJwt={wechatLoginByJwt}
        wechatLoginEnabled
      />
    );
  }

  draftStorage.setMemberId(user.id);

  if (location.pathname === '/login') {
    const destination = routeState?.from?.startsWith('/')
      ? routeState.from
      : '/projects';
    return <Navigate to={destination} replace />;
  }

  return (
    <>
      <Routes>
        <Route
          path="*"
          element={<VisualWorkspace user={user} onLogout={logout} />}
        />
      </Routes>
      {needBindInfo && (
        <BindInfoModal onBind={bindInfo} onLogout={logout} />
      )}
    </>
  );
};

function App() {
  if (process.env.REACT_APP_USE_LEGACY_UI === 'true') {
    return <LegacyApp />;
  }

  return (
    <BrowserRouter>
      <RoutedApp />
    </BrowserRouter>
  );
}

export default App;
